import type { RouterLinkProps } from 'vue-router'

import { defineStore } from 'pinia'
import { ref } from 'vue'

import { DOCTYPES } from '@/constants/doctypes'
import { PERMISSIONS } from '@/constants/permissions'
import { routeCompanyHome } from '@/routers/routes/mainRoutes/routeSettings/companyRoutes.ts'
import { useFrappeNestCustomAPIStore } from '@/stores/apis/frappeNestCustomAPI.ts'
import { useAuthStore } from '@/stores/auth'

const useSideNavStore = defineStore('useSideNavStore', () => {
  // stores
  const authStore = useAuthStore()
  const frappeNestCustomAPIStore = useFrappeNestCustomAPIStore()

  // refs
  const hasEmailInboxes = ref(false)
  const initialized = ref(false)
  const myCompany = ref<null | RouterLinkProps['to']>(null)

  let initializePromise: null | Promise<void> = null

  // actions
  const loadMyCompany = async (): Promise<void> => {
    if (!authStore.hasOnePermission(DOCTYPES.COMPANY, [PERMISSIONS.READ])) {
      myCompany.value = null
      return
    }

    const companyList = await frappeNestCustomAPIStore.getCompanyList({
      fields: ['name'],
    })

    const myCompanyId = companyList.pop()?.name
    if (myCompanyId) {
      myCompany.value = { name: routeCompanyHome.name, params: { id: myCompanyId } }
      return
    }

    myCompany.value = null
  }

  const loadEmailInboxes = async (): Promise<void> => {
    if (!authStore.hasOnePermission(DOCTYPES.COMMUNICATION, [PERMISSIONS.READ])) {
      hasEmailInboxes.value = false
      return
    }

    const response = await frappeNestCustomAPIStore.getEmailAccountsForUser()
    hasEmailInboxes.value = Boolean(response && response.email_accounts.length > 0)
  }

  const initialize = async (): Promise<void> => {
    if (initialized.value) return
    if (initializePromise) {
      await initializePromise
      return
    }

    initializePromise = Promise.allSettled([loadMyCompany(), loadEmailInboxes()])
      .then((results) => {
        results.forEach((result) => {
          if (result.status === 'rejected') {
            console.debug('[SideNav] initialize skipped/failed:', result.reason)
          }
        })
        initialized.value = true
      })
      .finally(() => {
        initializePromise = null
      })

    await initializePromise
  }

  return {
    // refs
    hasEmailInboxes,
    initialized,
    myCompany,
    // actions
    initialize,
    loadEmailInboxes,
    loadMyCompany,
  }
})

export { useSideNavStore }
